import { DashboardLayout } from '@/components/app/DashboardLayout'
import { StatCard } from '@/components/app/StatCard'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AuthAPI, AdminAPI } from '@/services/api'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2, Users, Baby, ClipboardList, Calendar, FileText } from 'lucide-react'

const DASHBOARD_REFRESH_MS = 120_000

export const AdminDashboard = () => {
  const [user, setUser] = useState<any>(null)
  const [stats, setStats] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const fallbackAdminUser = { firstName: 'Admin', lastName: '', email: '', role: 'ADMIN' } as any

  useEffect(() => {
    let mounted = true

    const fetchData = async (showLoader = false) => {
      if (showLoader) {
        setIsLoading(true)
      }

      try {
        const [userResult, statsResult] = await Promise.allSettled([
          AuthAPI.me(),
          AdminAPI.getStats(),
        ])

        if (!mounted) {
          return
        }

        if (userResult.status === 'fulfilled') {
          setUser(userResult.value.user)
        }

        if (statsResult.status === 'fulfilled') {
          setStats(statsResult.value)
          setLoadError(null)
        } else {
          console.error('Failed to fetch admin stats', statsResult.reason)
          setLoadError('Admin dashboard data is unavailable. Please verify backend connectivity and try again.')
        }
      } catch (err) {
        console.error("Failed to fetch admin dashboard data", err)
        if (mounted) {
          setLoadError('Admin dashboard data is unavailable. Please verify backend connectivity and try again.')
        }
      } finally {
        if (mounted && showLoader) {
          setIsLoading(false)
        }
      }
    }

    fetchData(true)
    const intervalId = setInterval(() => {
      fetchData(false)
    }, DASHBOARD_REFRESH_MS)

    return () => {
      mounted = false
      clearInterval(intervalId)
    }
  }, [])

  if (isLoading) {
    return (
      <DashboardLayout user={user || fallbackAdminUser}>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  if (!stats) {
    return (
      <DashboardLayout user={user || fallbackAdminUser}>
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
          <p className="text-muted-foreground">
            {loadError ?? 'Could not load dashboard data. Please try refreshing the page.'}
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  const questionnaire = stats.activeQuestionnaire

  return (
    <DashboardLayout user={user || fallbackAdminUser}>
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Quick overview of platform status and admin actions
            </p>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 h-auto">
            <TabsTrigger value="overview" className="py-2">Overview</TabsTrigger>
            <TabsTrigger value="questionnaire" className="py-2">Questionnaire</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard
                title="Total Users"
                value={(stats.totalUsers ?? 0).toLocaleString()}
                icon={Users}
                description="Registered parents"
              />
              <StatCard
                title="Total Children"
                value={(stats.totalChildren ?? 0).toLocaleString()}
                icon={Baby}
                description="Active profiles"
              />
              <StatCard
                title="Total Assessments"
                value={(stats.totalAssessments ?? 0).toLocaleString()}
                icon={ClipboardList}
                description="All time"
              />
              <StatCard
                title="This Month"
                value={(stats.assessmentsThisMonth ?? 0).toLocaleString()}
                icon={Calendar}
                description={`${stats.assessmentsToday ?? 0} today`}
              />
            </div>

            <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Assessment Snapshot</CardTitle>
                <CardDescription>Current assessment counts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-lg bg-card p-4 border border-border">
                    <p className="text-sm text-muted-foreground">Today</p>
                    <p className="text-2xl font-semibold">{(stats.assessmentsToday ?? 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg bg-card p-4 border border-border">
                    <p className="text-sm text-muted-foreground">This Week</p>
                    <p className="text-2xl font-semibold">{(stats.assessmentsThisWeek ?? 0).toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg bg-card p-4 border border-border">
                    <p className="text-sm text-muted-foreground">This Month</p>
                    <p className="text-2xl font-semibold">{(stats.assessmentsThisMonth ?? 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="questionnaire" className="space-y-4">
            <Card className="border-transparent bg-muted/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-6 w-6 shrink-0 text-primary" aria-hidden />
                  Active Questionnaire
                </CardTitle>
                <CardDescription>Current assessment configuration</CardDescription>
              </CardHeader>
              <CardContent>
                {questionnaire ? (
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 bg-card/90 rounded-lg border border-border hover:bg-card transition-all duration-300">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">{questionnaire.title}</h3>
                        <Badge variant="default" className="bg-green-600 hover:bg-green-700">Active</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{questionnaire.description}</p>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="text-muted-foreground">
                          <span className="font-medium">Age Range:</span> {questionnaire.minAge}-{questionnaire.maxAge} months
                        </span>
                        <span className="text-muted-foreground font-medium">
                          {(questionnaire._count?.questions ?? 0).toLocaleString()} Questions
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                      <Link to="/dashboard/admin/questionnaires" className="w-full sm:w-auto">
                        <Button variant="outline" className="w-full">View Details</Button>
                      </Link>
                      <Link to="/dashboard/admin/questionnaires" className="w-full sm:w-auto">
                        <Button variant="default" className="w-full">Edit Questions</Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <p className="text-sm text-amber-900 dark:text-amber-200">
                      {loadError || 'No active questionnaire found. Create or activate one in Questionnaire Management.'}
                    </p>
                    <Link to="/dashboard/admin/questionnaires">
                      <Button variant="default">Go To Questionnaire Management</Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
